import type { RendererHealthReport } from '@m43/bridge-interface';
import { electronBridge } from '../electron-bridge/electron-bridge';

/**
 * This window's health, told to the Electron main process, which keeps the launch's record and
 * reports it to Device Manager. On the web, and under an Electron main process too old to listen,
 * every call does nothing.
 *
 * Only what says whether the session loaded: the window started, and its bootstrap worked or did
 * not. Plus the realtime connection, for connectivity. The window's uncaught errors are not session
 * health and are not sent; they stay in its own logs.
 *
 * Never throws: it runs on startup paths, where a second failure would be worse than a missing
 * report.
 */

function report(healthReport: RendererHealthReport): void {
    try {
        if (electronBridge && 'reportSessionHealth' in electronBridge) {
            electronBridge.reportSessionHealth?.(healthReport);
        }
    } catch {
        // Never.
    }
}

/** First, before anything else can go wrong: this window's code is running. */
export function reportRendererStarted(): void {
    report({ type: 'renderer-started' });
}

/** The bootstrap call succeeded and the window has its data: for the first window, the boot succeeded. */
export function reportBootstrapSucceeded(): void {
    report({ type: 'bootstrap-succeeded' });
}

export function reportBootstrapFailed(status: string): void {
    report({ type: 'bootstrap-failed', status });
}

/** The realtime connection. Main counts it only from the launch's primary window. */
export function reportSocketStatus(status: 'connected' | 'disconnected', reason?: string): void {
    report({ type: 'socket-status', status, ...(reason ? { reason } : {}) });
}
